import { MaterialSymbol } from '@florencelabs/icons';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AppointmentSummaryActions } from '@provider-directory/components/appointment-summary/appointment-summary-actions';
import { AppointmentSummary } from '@provider-directory/components/appointment-summary/appointment-summary.component';
import {
  DefaultSchedulingError,
  AppointmentUnavailableError,
} from '@provider-directory/components/scenes/errors';
import { SplitScreen } from '@provider-directory/components/split-screen/split-screen.component';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { useScheduleAppointment } from '@provider-directory/hooks/useScheduleAppointment';
import { useAnalytics } from '../analytics/analytics-provider';

export default function Review(): ReactElement {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const router = useRouter();
  const { schedule, isLoading, error } = useScheduleAppointment();
  const { provider } = useProvider();

  const handleChangeAppointment = (): void => {
    analytics.track('PRESSED_CHANGE_APPOINTMENT', {
      appType: 'provider-directory',
      linkContextPage: 'SchedulingReview',
      linkContextComponent: 'AppointmentSummary',
    });
    router.push('select-time');
  };

  if (error === 'unavailable-slot') {
    return <AppointmentUnavailableError />;
  }

  if (error || !provider) {
    return <DefaultSchedulingError errorCode={error} />;
  }

  return (
    <SplitScreen
      isOriginalOrder={true}
      additionalClassnames={{
        left: {
          children: 'px-6 pt-6 desktop:p-0 desktop:pt-10 desktop:pr-20',
        },
        right: {
          children: 'desktop:max-w-[584px]',
          screen: 'bg-transparent bg-none flex-0',
        },
      }}
    >
      <div
        data-testid={'appointment-review'}
        className={'flex w-full flex-col items-start'}
      >
        <Link
          href={'/providers/appointment/request/insurance'}
          data-testid={'go-back-btn'}
          className={
            'mb-6 flex items-center gap-1 mobile:text-body-medium-semibold desktop:mb-14 desktop:hidden desktop:text-body-large-semibold print:hidden'
          }
        >
          <MaterialSymbol name={'chevron_left'} size={20} />
          {t('schedule.back')}
        </Link>
        <h1
          data-testid={'review-page-title'}
          className={
            'font-serif text-display-large desktop:text-display-xlarge'
          }
        >
          {t('schedule.reviewAppointmentDetails')}
        </h1>
        <p className={'mt-3 text-body-medium'}>
          {t('schedule.reviewAppointmentDetailsText')}
        </p>
      </div>
      <AppointmentSummary>
        <AppointmentSummaryActions
          action1={{
            text: t('schedule.changeAppointment'),
            onClick: handleChangeAppointment,
            className: 'order-2 min-[640px]:order-1 text-button-medium',
          }}
          action2={{
            text: t('schedule.scheduleAppointment'),
            onClick: () => {
              if (provider.ehrExternalId) {
                analytics.completedFormEvent({
                  formStep: 'AppointmentReview',
                  linkContextPage: 'SchedulingReview',
                  provider,
                });
                schedule(provider.ehrExternalId);
              }
            },
            isLoading,
            className: 'order-1 min-[640px]:order-2 text-button-medium',
          }}
        />
      </AppointmentSummary>
    </SplitScreen>
  );
}
